import type { JSX, ReactNode } from 'react'

import { useReadOnly } from '../components/read-only.tsx'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader
} from '../components/ui/card.tsx'
import { cn } from '../lib/cn.ts'

/**
 * How much horizontal room a panel takes.
 *
 * `form` is the default and is a measure cap, not decoration: a column of
 * fields wider than Tailwind's `2xl` container is hard to read and hard to
 * scan back along.
 *
 * `wide` fills the shell's `<main>`, which sets no width of its own. It is for
 * a panel whose content is a table — a fifteen-column report does not fit in a
 * form measure, and a `className` override would hide that from the next
 * screen that needs one. Choose it deliberately; most screens are `form`.
 */
const WIDTHS = {
  form: 'max-w-2xl',
  wide: 'max-w-none'
} as const satisfies Record<string, string>

export interface PanelProps {
  'data-testid': string
  /**
   * The panel heading. Omit it when a parent already drew the heading, such
   * as a drill sheet, so the section does not carry a second one.
   */
  title?: ReactNode
  description?: ReactNode
  /** A control at the top right, on the title's row. */
  action?: ReactNode
  children: ReactNode
  width?: keyof typeof WIDTHS
  className?: string
}

/**
 * Panel — the console's unit of routed content.
 *
 * Every screen is a titled section, so the heading level and spacing are decided
 * once here instead of per screen. Screens compose Panels; they do not reach
 * for `Card` directly.
 *
 * @param props - Panel props.
 * @param props.title - The panel heading. Omit it when a parent already drew one.
 * @param props.description - Optional supporting line under the title.
 * @param props.action - Optional control at the header's top right.
 * @param props.children - Panel body.
 * @param props.width - How much horizontal room to take; see `WIDTHS`.
 * @param props.className - Extra classes.
 * @returns The rendered panel.
 */
export function Panel({
  'data-testid': testId,
  title,
  description,
  action,
  children,
  width = 'form',
  className
}: PanelProps): JSX.Element {
  const readOnly = useReadOnly()
  const room = readOnly ? 'wide' : width
  const showTitle = title !== undefined && title !== null && title !== ''
  return (
    <Card
      data-testid={testId}
      className={cn(
        'gap-6 rounded-none border-0 border-t-(length:--rule-width) border-rule bg-transparent py-6 text-foreground shadow-none',
        // A dialog and a drill sheet each draw their own rule frame and
        // padding. A second top rule and top padding inside either would stack.
        'in-data-[slot=dialog-content]:border-t-0 in-data-[slot=dialog-content]:pt-0',
        'in-data-[slot=sheet-content]:border-t-0 in-data-[slot=sheet-content]:pt-0',
        WIDTHS[room],
        className
      )}
    >
      {showTitle || description || action ? (
        <CardHeader className="px-0">
          {/* The section title sets no tracking, so the page title's tight
            tracking would carry into the dialog without the reset. */}
          {showTitle ? (
            <h1 className="type-page-title in-data-[slot=dialog-content]:type-section-title in-data-[slot=dialog-content]:tracking-normal in-data-[slot=sheet-content]:type-section-title in-data-[slot=sheet-content]:tracking-normal">
              {title}
            </h1>
          ) : null}
          {description ? (
            // `CardDescription` always adds `text-sm` and `text-muted-foreground`.
            // Those utilities follow `type-lede` in the stylesheet and would hide it.
            <div data-slot="card-description" className="type-lede text-pretty">
              {description}
            </div>
          ) : null}
          {action ? <CardAction>{action}</CardAction> : null}
        </CardHeader>
      ) : null}
      <CardContent className="grid gap-4 px-0">{children}</CardContent>
    </Card>
  )
}
